/**
 * Intervals and the beats they produce.
 *
 * Laying a temperament by ear does not mean hearing "in tune" — it means
 * hearing the *right amount* of out of tune. Every tempered interval beats at a
 * rate the temperament itself dictates, and a tuner sets that rate by counting.
 * This module works out what rate to expect, so the measured rate has something
 * to be measured against.
 */

export interface Interval {
  readonly id: string;
  readonly name: string;
  /** Semitones from the lower note to the upper. */
  readonly semitones: number;
  /**
   * The just ratio, upper over lower. Beats arise where the harmonic series of
   * the two notes ought to coincide: for a 3:2 fifth, the third harmonic of the
   * lower note against the second of the upper.
   */
  readonly numerator: number;
  readonly denominator: number;
}

export const INTERVALS: readonly Interval[] = [
  { id: "unison", name: "Unison", semitones: 0, numerator: 1, denominator: 1 },
  { id: "minor-third", name: "Minor third", semitones: 3, numerator: 6, denominator: 5 },
  { id: "major-third", name: "Major third", semitones: 4, numerator: 5, denominator: 4 },
  { id: "fourth", name: "Fourth", semitones: 5, numerator: 4, denominator: 3 },
  { id: "fifth", name: "Fifth", semitones: 7, numerator: 3, denominator: 2 },
  { id: "minor-sixth", name: "Minor sixth", semitones: 8, numerator: 8, denominator: 5 },
  { id: "major-sixth", name: "Major sixth", semitones: 9, numerator: 5, denominator: 3 },
  { id: "octave", name: "Octave", semitones: 12, numerator: 2, denominator: 1 },
];

export function intervalById(id: string): Interval {
  return INTERVALS.find((i) => i.id === id) ?? INTERVALS[4]!;
}

/**
 * Frequency at which the two notes' harmonic series meet — the partial that
 * does the beating, and so the band to listen in.
 */
export function coincidentPartial(lowerHz: number, interval: Interval): number {
  return lowerHz * interval.numerator;
}

/**
 * Beats per second produced by this pair of frequencies at the given interval.
 *
 * Positive means the upper note is flat of just, negative means sharp; the
 * audible rate is the magnitude. Most tempered fifths are narrow, so in a
 * typical temperament this comes out positive.
 */
export function signedBeatRate(lowerHz: number, upperHz: number, interval: Interval): number {
  return interval.numerator * lowerHz - interval.denominator * upperHz;
}

export function beatRate(lowerHz: number, upperHz: number, interval: Interval): number {
  return Math.abs(signedBeatRate(lowerHz, upperHz, interval));
}

/**
 * Envelope sample rate to use when measuring this interval's beats.
 *
 * The band examined must be narrow enough to exclude the neighbouring partials,
 * which sit one fundamental apart — otherwise they land inside the band and
 * beat against each other, producing a confident reading of the wrong thing.
 * On low notes that forces a narrow band, and a narrow band cannot follow a
 * fast beat; the floor keeps roughly 15 beats per second measurable, which is
 * well beyond anything anyone counts by ear.
 */
export function envelopeRateFor(lowerHz: number): number {
  return Math.min(200, Math.max(60, lowerHz / 2));
}

/** Fastest beat this envelope rate can resolve. */
export function maxBeatRateFor(envelopeRate: number): number {
  return envelopeRate / 4;
}
